import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../shared/components/alert/Alert';
import { Button } from '../../../shared/components/button/Button';
import LoadingSpinner from '../../../shared/components/UIElements/LoadingSpinner';
import { AuthContext } from '../../../shared/context/auth-context';
import { handleEidChange, handleNameChange } from '../../../shared/util/handleValueChange';
import { DropDownRegistration } from './DropDownRegistration';
import { useSignupScreen2 } from './useSignupScreen2';

interface SignupScreen2Props {
  erv: string | boolean | undefined;
  name: string | undefined;
  ervFieldName: string;
  departments: any[] | undefined;
  subDepartments: any[] | undefined;
  regions: any[] | undefined;
  fandf: boolean;
  error: string | undefined;
  isHub: boolean;
  handleContinue: (dataObj: any) => void;
  onOpenModal: (params: { modalText: string; questionTitle: string }) => void;
}

export function SignupScreen2({
  erv,
  name: initialName,
  ervFieldName,
  departments,
  subDepartments,
  regions: initialRegions,
  fandf,
  error,
  isHub,
  handleContinue,
  onOpenModal,
}: SignupScreen2Props) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();

  const {
    errorMessage,
    region,
    regions,
    regionGroupArr,
    regionGroup,
    regionsOptions,
    department,
    subDepartment,
    subDepts,
    nameError,
    eidError,
    regionGroupError,
    regionError,
    departmentError,
    subDepartmentError,
    isLoading,
    nameInputRef,
    eidInputRef,
    handleContinue: handleContinueForm,
    onSetRegionGroup,
    onSetDept,
    handleRegion,
    handleSubDepartment,
    setName,
    setEid,
  } = useSignupScreen2({
    erv,
    name: initialName,
    ervFieldName,
    departments,
    subDepartments,
    regions: initialRegions,
    fandf,
    error,
    isHub,
    handleContinue,
    onOpenModal,
  });

  return (
    <>
      <h1>{t('R026')}</h1>
      {!auth.isLoggedIn && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleContinueForm();
          }}
        >
          {errorMessage && (
            <Alert
              id="error-alert"
              isVisible={!!errorMessage}
              message={errorMessage}
              type="error"
              size="sm"
              isAssessive
              dataTestId="signup-screen-error"
            />
          )}
          {!initialName && (
            <>
              <label className="not_logged_label" htmlFor="name">
                {nameError ? `${t('error.prefix')} ${t('R027')}` : t('R027')}
              </label>
              <input
                ref={nameInputRef}
                type="text"
                className="code_input"
                id="name"
                name="name"
                tabIndex={0}
                aria-invalid={nameError}
                aria-describedby={nameError ? 'name-error-alert' : undefined}
                onChange={(e) => handleNameChange(e, setName)}
                data-testid="signup-first-name-input"
              />
              <Alert
                id="name-error-alert"
                isVisible={nameError}
                message={nameError ? t('R055') : ''}
                type="error"
                size="sm"
                dataTestId="signup-first-name-error"
              />
            </>
          )}
          {erv === 'yes' && ervFieldName && !fandf && !isHub && (
            <>
              <label className="not_logged_label" htmlFor="erv">
                {eidError ? `${t('error.prefix')} ${ervFieldName}` : ervFieldName}
              </label>
              <input
                ref={eidInputRef}
                type="text"
                className="code_input"
                id="erv"
                name="eid"
                aria-invalid={eidError}
                aria-describedby={eidError ? 'erv-error-alert' : undefined}
                onChange={(e) => handleEidChange(e, setEid)}
                data-testid="signup-erv-id-input"
              />
              <Alert
                id="erv-error-alert"
                isVisible={eidError}
                message={eidError ? `${t('R060')}${ervFieldName}` : ''}
                type="error"
                size="sm"
                dataTestId="signup-erv-id-error"
              />
            </>
          )}
          {regionGroupArr && regionGroupArr.length > 0 && (
            <DropDownRegistration
              activeItem={regionGroup}
              data={regionGroupArr}
              show="profile"
              placeholder={t('R028')}
              setActiveItem={onSetRegionGroup}
              wtfText={t('R032')}
              onOpenModal={onOpenModal}
              selectId="regionGroup"
              required
              hasError={regionGroupError}
              errorMessage={regionGroupError ? t('R057') : ''}
            />
          )}

          {regionsOptions && regionsOptions.length > 0 && regionGroup && (
            <DropDownRegistration
              activeItem={region}
              data={regionsOptions}
              show="profile"
              placeholder={t('R029')}
              setActiveItem={handleRegion}
              wtfText={t('R032')}
              onOpenModal={onOpenModal}
              selectId="regionsOptions"
              required
              hasError={regionError || regionGroupError}
              errorMessage={regionError || regionGroupError ? t('R057') : ''}
            />
          )}
          {regions && regions.length > 0 && regionGroupArr.length === 0 && (
            <DropDownRegistration
              activeItem={region}
              data={regions}
              placeholder={t('R028')}
              show="profile"
              setActiveItem={handleRegion}
              wtfText={t('R032')}
              onOpenModal={onOpenModal}
              selectId="regions"
              required
              hasError={regionError}
              errorMessage={regionError ? t('R057') : ''}
            />
          )}

          {!fandf && departments && departments[0] && departments[0].title.length > 0 && (
            <DropDownRegistration
              activeItem={department}
              data={departments}
              show="profile"
              placeholder={t('R030')}
              setActiveItem={onSetDept}
              wtfText={t('R033')}
              onOpenModal={onOpenModal}
              selectId="department"
              required
              hasError={departmentError}
              errorMessage={departmentError ? t('R058') : ''}
            />
          )}
          {!fandf && subDepts && subDepts.length > 0 && (
            <DropDownRegistration
              activeItem={subDepartment}
              data={subDepts}
              show="profile"
              placeholder={t('R031')}
              setActiveItem={handleSubDepartment}
              wtfText={t('R033')}
              onOpenModal={onOpenModal}
              selectId="subDepartment"
              required
              hasError={subDepartmentError}
              errorMessage={subDepartmentError ? t('R059') : ''}
            />
          )}
          {isLoading && <LoadingSpinner asOverlay />}
          {!isLoading && (
            <Button type="submit" id="continue" dataTestId="signup-continue-button">
              {t('R013')}
            </Button>
          )}
        </form>
      )}
    </>
  );
}
